// The pronunciation curriculum: the sound inventory and the rules that change it in running speech.
//
// This is content, not logic — it is data so the UI can render every item as a tappable row that
// speaks itself, and so it stays reviewable in one place. The English approximations are honest
// about where they break down; the ones learners get wrong (b/p aspiration, j/q/x vs zh/ch/sh,
// the -i after z/c/s, ü) carry an explicit warning rather than a comfortable half-truth.

export interface SoundRow {
  /** The pinyin letter(s). */
  sound: string;
  /** A full syllable that is safe to pronounce and to send to speech synthesis. */
  example: string;
  hanzi: string;
  meaning: string;
  /** English approximation, or a plain description where no English sound is close. */
  note: string;
  /** Set when the obvious English guess is actively wrong. */
  warn?: string;
}

export interface SoundGroup {
  id: string;
  title: string;
  intro: string;
  rows: SoundRow[];
}

export const INITIALS: SoundGroup[] = [
  {
    id: 'labial',
    title: 'b p m f — lips',
    intro:
      'The pair b/p is not voiced vs unvoiced as in English. Both are unvoiced; what separates them is the puff of air. Hold a sheet of paper in front of your mouth: p moves it, b does not.',
    rows: [
      { sound: 'b', example: 'bā', hanzi: '八', meaning: 'eight', note: 'Like the p in "spin" — no puff of air.', warn: 'Not the b of "bin", which is voiced.' },
      { sound: 'p', example: 'pà', hanzi: '怕', meaning: 'to fear', note: 'Like the p in "pin" — a strong puff of air.' },
      { sound: 'm', example: 'mā', hanzi: '妈', meaning: 'mother', note: 'As in English "ma".' },
      { sound: 'f', example: 'fēi', hanzi: '飞', meaning: 'to fly', note: 'As in English "fee".' },
    ],
  },
  {
    id: 'alveolar',
    title: 'd t n l — tongue tip',
    intro: 'Same aspiration contrast as b/p: d is the unaspirated one, t the one with the puff.',
    rows: [
      { sound: 'd', example: 'dà', hanzi: '大', meaning: 'big', note: 'Like the t in "stop" — no puff.', warn: 'Not the d of "dog".' },
      { sound: 't', example: 'tā', hanzi: '他', meaning: 'he', note: 'Like the t in "top" — with a puff.' },
      { sound: 'n', example: 'nǐ', hanzi: '你', meaning: 'you', note: 'As in English "knee".' },
      { sound: 'l', example: 'lái', hanzi: '来', meaning: 'to come', note: 'As in English "lie".' },
    ],
  },
  {
    id: 'velar',
    title: 'g k h — back of the mouth',
    intro: 'The same pair again for g/k. h is further back and rougher than the English h.',
    rows: [
      { sound: 'g', example: 'gè', hanzi: '个', meaning: '(measure word)', note: 'Like the k in "skill" — no puff.', warn: 'Not the g of "go".' },
      { sound: 'k', example: 'kàn', hanzi: '看', meaning: 'to look', note: 'Like the k in "kill" — with a puff.' },
      { sound: 'h', example: 'hǎo', hanzi: '好', meaning: 'good', note: 'Raspier than English h, closer to the ch in Scottish "loch".' },
    ],
  },
  {
    id: 'palatal',
    title: 'j q x — flat tongue, smiling',
    intro:
      'Spread your lips as if smiling and press the flat of your tongue against the front of the roof of your mouth. These three only ever appear before i or ü, and a ü written after them drops its dots: ju is really jü.',
    rows: [
      { sound: 'j', example: 'jī', hanzi: '鸡', meaning: 'chicken', note: 'Roughly the j in "jeep", but flatter and with no puff.' },
      { sound: 'q', example: 'qī', hanzi: '七', meaning: 'seven', note: 'The same shape as j, but with a strong puff of air.', warn: 'Never a "k" sound — q is nothing like the English qu.' },
      { sound: 'x', example: 'xià', hanzi: '下', meaning: 'below', note: 'Between the English s and sh, said with a smile.' },
    ],
  },
  {
    id: 'retroflex',
    title: 'zh ch sh r — curled tongue',
    intro:
      'Curl your tongue tip back towards the roof of your mouth. These are the ones English speakers most often collapse into j/ch/sh — keeping them apart from j/q/x is what makes you sound clear.',
    rows: [
      { sound: 'zh', example: 'zhè', hanzi: '这', meaning: 'this', note: 'Like "j" in "jam" but with the tongue curled back, no puff.' },
      { sound: 'ch', example: 'chī', hanzi: '吃', meaning: 'to eat', note: 'The curled-back version of ch in "church", with a puff.' },
      { sound: 'sh', example: 'shū', hanzi: '书', meaning: 'book', note: 'Like "sh" in "shoe" but with the tongue curled back.' },
      { sound: 'r', example: 'rén', hanzi: '人', meaning: 'person', note: 'Between the English r and the s in "measure"; tongue curled, lips not rounded.' },
    ],
  },
  {
    id: 'sibilant',
    title: 'z c s — flat tongue, front teeth',
    intro: 'Tongue tip right behind the upper front teeth, flat. c is the one most often mispronounced.',
    rows: [
      { sound: 'z', example: 'zài', hanzi: '在', meaning: 'at', note: 'Like the ds in "beds".' },
      { sound: 'c', example: 'cài', hanzi: '菜', meaning: 'dish; vegetable', note: 'Like the ts in "cats", with a strong puff.', warn: 'Never an s or k sound.' },
      { sound: 's', example: 'sān', hanzi: '三', meaning: 'three', note: 'As in English "sun".' },
    ],
  },
];

export const FINALS: SoundGroup[] = [
  {
    id: 'simple',
    title: 'Single vowels',
    intro: 'The building blocks. ü is the one with no English equivalent — and the one worth drilling early.',
    rows: [
      { sound: 'a', example: 'mā', hanzi: '妈', meaning: 'mother', note: 'Open, as in "father".' },
      { sound: 'o', example: 'wǒ', hanzi: '我', meaning: 'I', note: 'As in "war", with rounded lips.' },
      { sound: 'e', example: 'hē', hanzi: '喝', meaning: 'to drink', note: 'Unrounded, back of the mouth — the vowel in "duh".', warn: 'Not the e of "bed".' },
      { sound: 'i', example: 'yī', hanzi: '一', meaning: 'one', note: 'Long, as in "see".' },
      { sound: 'u', example: 'wǔ', hanzi: '五', meaning: 'five', note: 'As in "food", lips firmly rounded.' },
      { sound: 'ü', example: 'nǚ', hanzi: '女', meaning: 'woman', note: 'Say "ee", then round your lips without moving your tongue.', warn: 'Written u after j, q, x and y — ju, qu, xu, yu are all ü.' },
    ],
  },
  {
    id: 'buzz',
    title: 'The buzzing -i',
    intro:
      'After z, c, s, zh, ch, sh and r, the letter i is not "ee" at all. It is a buzz made by holding the tongue exactly where the consonant left it.',
    rows: [
      { sound: 'zi', example: 'zì', hanzi: '字', meaning: 'character', note: 'Hold the z and add voice — "dzzz".', warn: 'Not "zee".' },
      { sound: 'shi', example: 'shì', hanzi: '是', meaning: 'to be', note: 'Curl the tongue for sh and voice it — "shrr".', warn: 'Not "she".' },
      { sound: 'ri', example: 'rì', hanzi: '日', meaning: 'sun; day', note: 'The r held and voiced, tongue curled.' },
    ],
  },
  {
    id: 'compound',
    title: 'Vowel combinations',
    intro: 'Glide smoothly from the first vowel into the second; do not clip them into two syllables.',
    rows: [
      { sound: 'ai', example: 'ài', hanzi: '爱', meaning: 'to love', note: 'As in "eye".' },
      { sound: 'ei', example: 'bēi', hanzi: '杯', meaning: 'cup', note: 'As in "day".' },
      { sound: 'ao', example: 'hǎo', hanzi: '好', meaning: 'good', note: 'As in "how".' },
      { sound: 'ou', example: 'gǒu', hanzi: '狗', meaning: 'dog', note: 'As in "low".' },
      { sound: 'ie', example: 'xiè', hanzi: '谢', meaning: 'to thank', note: 'As in "yeah".' },
      { sound: 'uo', example: 'duō', hanzi: '多', meaning: 'many', note: 'A "w" gliding into "or".' },
      { sound: 'üe', example: 'yuè', hanzi: '月', meaning: 'moon; month', note: 'ü gliding into "eh".' },
    ],
  },
  {
    id: 'nasal',
    title: 'Nasal endings',
    intro:
      '-n ends with the tongue tip at the teeth; -ng ends with the back of the tongue raised and the mouth open. This pair distinguishes real words, so it is worth separating carefully.',
    rows: [
      { sound: 'an', example: 'sān', hanzi: '三', meaning: 'three', note: 'Tongue tip forward, as in "ban".' },
      { sound: 'ang', example: 'máng', hanzi: '忙', meaning: 'busy', note: 'Back of tongue up, as in "song"; the vowel opens out.' },
      { sound: 'en', example: 'rén', hanzi: '人', meaning: 'person', note: 'As in "taken".' },
      { sound: 'eng', example: 'děng', hanzi: '等', meaning: 'to wait', note: 'As in "sung".' },
      { sound: 'in', example: 'xīn', hanzi: '心', meaning: 'heart', note: 'As in "sin".' },
      { sound: 'ing', example: 'tīng', hanzi: '听', meaning: 'to listen', note: 'As in "sing".' },
      { sound: 'ong', example: 'zhōng', hanzi: '中', meaning: 'middle', note: 'Closer to "oong" than "ong".' },
    ],
  },
  {
    id: 'er',
    title: 'er and the -r ending',
    intro: 'A vowel with the tongue curled. It also attaches to other syllables, especially in Beijing speech.',
    rows: [
      { sound: 'er', example: 'èr', hanzi: '二', meaning: 'two', note: 'Like the American "are".' },
      { sound: '-r', example: 'nǎr', hanzi: '哪儿', meaning: 'where', note: 'Curl the tongue at the end of the syllable; the -n before it disappears.' },
    ],
  },
];

export interface SandhiRule {
  id: string;
  title: string;
  rule: string;
  /** Why it happens, where a reason exists — rules with a reason are easier to keep. */
  why?: string;
  examples: { hanzi: string; written: string; spoken: string; meaning: string }[];
}

export const SANDHI: SandhiRule[] = [
  {
    id: 'third',
    title: 'Two third tones in a row',
    rule: 'When a third tone is followed by another third tone, the FIRST one is said as a second tone. The writing does not change.',
    why: 'Two full low-dipping tones back to back are slow and awkward to say, so the first one lifts.',
    examples: [
      { hanzi: '你好', written: 'nǐ hǎo', spoken: 'ní hǎo', meaning: 'hello' },
      { hanzi: '很好', written: 'hěn hǎo', spoken: 'hén hǎo', meaning: 'very good' },
      { hanzi: '可以', written: 'kě yǐ', spoken: 'ké yǐ', meaning: 'may; can' },
    ],
  },
  {
    id: 'half-third',
    title: 'A third tone before any other tone',
    rule: 'Before a first, second, fourth or neutral tone, the third tone loses its final rise: it just falls low and stays there.',
    why: 'The rise only exists to close the syllable when nothing follows. In connected speech there is no need for it.',
    examples: [
      { hanzi: '老师', written: 'lǎo shī', spoken: 'lăo shī (low, no rise)', meaning: 'teacher' },
      { hanzi: '好吃', written: 'hǎo chī', spoken: 'hăo chī (low, no rise)', meaning: 'tasty' },
    ],
  },
  {
    id: 'bu',
    title: '不 bù before a fourth tone',
    rule: '不 is normally bù (fourth tone), but before another fourth tone it becomes bú (second tone).',
    examples: [
      { hanzi: '不是', written: 'bù shì', spoken: 'bú shì', meaning: 'is not' },
      { hanzi: '不对', written: 'bù duì', spoken: 'bú duì', meaning: 'not right' },
      { hanzi: '不好', written: 'bù hǎo', spoken: 'bù hǎo (unchanged)', meaning: 'not good' },
    ],
  },
  {
    id: 'yi',
    title: '一 yī and what follows it',
    rule: 'Alone, when counting, and in ordinals 一 stays yī. Before a fourth tone it becomes yí; before a first, second or third tone it becomes yì.',
    examples: [
      { hanzi: '一个', written: 'yī gè', spoken: 'yí gè', meaning: 'one (item)' },
      { hanzi: '一天', written: 'yī tiān', spoken: 'yì tiān', meaning: 'one day' },
      { hanzi: '一起', written: 'yī qǐ', spoken: 'yì qǐ', meaning: 'together' },
      { hanzi: '第一', written: 'dì yī', spoken: 'dì yī (unchanged)', meaning: 'first' },
    ],
  },
  {
    id: 'neutral',
    title: 'The neutral tone',
    rule: 'Grammatical particles, the second half of a doubled word, and many noun suffixes lose their tone and are said short and light.',
    why: 'Its pitch is not free: it lands high after a first tone, and high after a third — the previous syllable decides.',
    examples: [
      { hanzi: '的', written: 'de', spoken: 'de (always light)', meaning: '(possessive particle)' },
      { hanzi: '看看', written: 'kàn kàn', spoken: 'kàn kan', meaning: 'to have a look' },
      { hanzi: '朋友', written: 'péng yǒu', spoken: 'péng you', meaning: 'friend' },
      { hanzi: '谢谢', written: 'xiè xiè', spoken: 'xiè xie', meaning: 'thank you' },
    ],
  },
];
